import { Controller } from "@hotwired/stimulus";

const MAX_FIELDS = 30;

export default class extends Controller {
  static targets = ["fieldList", "field"];
  static values = {
    fieldTypeLabels: Object,
    fieldLabelPlaceholder: String,
    fieldTypeAriaLabel: String,
    fieldLabelAriaLabel: String,
    requiredLabel: String,
    removeFieldAriaLabel: String,
  };

  addField() {
    if (this.fieldTargets.length >= MAX_FIELDS) return;

    const idx = this.fieldTargets.length;
    const prefix = `appeal_form[appeal_form_fields_attributes][${idx}]`;
    const row = document.createElement("div");
    row.className = "flex items-start gap-2 mb-3 p-3 border border-base-300 rounded-lg";
    row.dataset.appealFormBuilderTarget = "field";
    row.innerHTML = this.#fieldRowHTML(prefix, idx);
    this.fieldListTarget.appendChild(row);
  }

  removeField(event) {
    const row = event.currentTarget.closest('[data-appeal-form-builder-target="field"]');
    if (!row) return;

    const destroyFlag = row.querySelector('[data-appeal-form-builder-target="destroyFlag"]');
    if (destroyFlag) {
      destroyFlag.value = "1";
      row.classList.add("hidden");
    } else {
      row.remove();
    }
  }

  fieldTypeChanged(event) {
    const select = event.currentTarget;
    const row = select.closest('[data-appeal-form-builder-target="field"]');
    if (!row) return;
    const requiredLabel = row.querySelector('[data-appeal-form-builder-target="requiredLabel"]');
    if (requiredLabel) {
      requiredLabel.style.visibility = select.value === "instruction" ? "hidden" : "";
    }
  }

  #fieldRowHTML(prefix, idx) {
    const labels = this.fieldTypeLabelsValue;
    const types = ["short_text", "long_text", "checkbox", "instruction"];
    const opts = types
      .map((type) => `<option value="${type}">${this.#escHtml(labels[type] || type)}</option>`)
      .join("");
    const placeholder = this.#escHtml(this.fieldLabelPlaceholderValue || "");
    const requiredLabel = this.#escHtml(this.requiredLabelValue || "Required");
    const removeLabel = this.#escHtml(this.removeFieldAriaLabelValue || "Remove field");
    // A row added here is the same row the server renders, so it carries the
    // same names -- without these two, every field past the first announced as
    // an unlabelled select and an unlabelled text box (StreamCommand-wbcp).
    const typeLabel = this.#escHtml(this.fieldTypeAriaLabelValue || "Field type");
    const labelLabel = this.#escHtml(this.fieldLabelAriaLabelValue || "Field label");

    return `
      <input type="hidden" name="${prefix}[position]" value="${idx}">
      <div class="flex-1 grid grid-cols-1 sm:grid-cols-[auto_1fr_auto] gap-2 items-start">
        <select name="${prefix}[field_type]" class="select select-bordered select-sm w-36"
                aria-label="${typeLabel}"
                data-action="change->appeal-form-builder#fieldTypeChanged">
          ${opts}
        </select>
        <input type="text" name="${prefix}[label]" class="input input-bordered input-sm flex-1"
               placeholder="${placeholder}" aria-label="${labelLabel}" required>
        <div class="flex items-center gap-2">
          <label class="flex items-center gap-1 text-sm cursor-pointer"
                 data-appeal-form-builder-target="requiredLabel">
            <input type="checkbox" name="${prefix}[required]" value="1" class="checkbox checkbox-xs">
            ${requiredLabel}
          </label>
          <button type="button"
                  class="btn btn-ghost btn-sm btn-square text-error/70 hover:text-error"
                  aria-label="${removeLabel}"
                  data-action="click->appeal-form-builder#removeField">
            <i class="fa-solid fa-trash-can text-xs"></i>
          </button>
        </div>
      </div>
      <input type="hidden" name="${prefix}[_destroy]" value="0"
             data-appeal-form-builder-target="destroyFlag">
    `;
  }

  #escHtml(str) {
    return String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }
}
